import { action, computed, observable, reaction } from 'mobx';
import { DateTime } from 'luxon';

import httpFacade from 'http/httpFacade';
import { IntervalFetch } from 'http/intervalFetch';
import Log from 'helpers/log';
import { getDate, getLocalDateTime } from 'helpers/datetime';
import {
  ConsumptionCondition,
  OrderModel,
  SwipeDirection,
} from '../UserProfile/types';
import {
  OnlineOrderPlace,
  OnlineOrdersFilters,
  OnlineOrderStatus,
  OrderLocation,
} from './type';
import { MOBILE_DEVICES } from '../constants';
import {
  NewOnlineOrdersData,
  SocketDataType,
  DepositItemsRequestConfirmData,
  DepositItemsRequestCompleteData,
} from 'stores/Socket/types';
import { sortByAccessor } from 'helpers/accessors';
import { PromiseService } from 'services/PromiseService';

class OnlineOrdersStore {
  @observable id: string;
  @observable searchValue: string = '';
  @observable ordersData: OrderModel[] = [];
  @observable currentDate: DateTime = getLocalDateTime();
  @observable activeDate: DateTime = getLocalDateTime();
  @observable intervalFetch: IntervalFetch = new IntervalFetch();
  @observable locations: OrderLocation[] = [];
  @observable orders: OrderModel[] = [];
  @observable depositItemsConfirmation: Map<string, string> = new Map<
    string,
    string
  >();

  @observable filters: OnlineOrdersFilters = {
    places: [OnlineOrderPlace.ALL],
    statuses: [OnlineOrderStatus.PENDING],
  };

  @observable private _promiseService = new PromiseService();
  private markRowsTimeout;

  private vorwerkOnlineOrdersSort = sortByAccessor({
    accessor: 'queueNumber',
    desc: false,
  });

  constructor() {
    this.intervalFetch.setRequest(this.fetchOrders);

    reaction(
      () => this.activeDate,
      async () => {
        await this._promiseService.add(this.fetchOrders);
      },
    );
  }

  @action.bound
  async init() {
    try {
      [{ data: this.locations }] = await this._promiseService.add(async () =>
        Promise.all([
          await httpFacade.onlineOrders.fetchOrderLocations(),
          await this.intervalFetch.start(),
        ]),
      );
      this.fetchDepositItemsRequests();
    } catch (error) {
      Log.warn(error);
    }
    this.markTableRows();
  }

  @action.bound
  applyFilter(filters: OnlineOrdersFilters) {
    this.filters = filters;

    this.orders = this.ordersData.filter(order => {
      const matchesPlace =
        !filters.places.length ||
        filters.places.includes(OnlineOrderPlace.ALL) ||
        filters.places.includes(order.deliveryLocation?.id || '') ||
        (filters.places.includes(OnlineOrderPlace.TAKE_AWAY) &&
          order.consumptionCondition === ConsumptionCondition.TAKE_AWAY) ||
        (filters.places.includes(OnlineOrderPlace.IN_PLACE) &&
          order.consumptionCondition === ConsumptionCondition.IN_PLACE);

      const matchesStatus =
        filters.statuses.includes(OnlineOrderStatus.ALL) ||
        (filters.statuses.includes(OnlineOrderStatus.DELIVERED) &&
          order.orderProcessingFinished) ||
        (filters.statuses.includes(OnlineOrderStatus.PENDING) &&
          !order.orderProcessingFinished);

      const matchesSearchValue = this.searchValue
        ? this.regExpQueNumber.test(String(order.queueNumber)) ||
          order.items.some(item => this.regExpItemTitle.test(item.title))
        : true;

      return matchesPlace && matchesStatus && matchesSearchValue;
    });
  }

  @computed
  get regExpItemTitle() {
    return new RegExp(this.searchValue, 'i');
  }

  @computed
  get orderLocations() {
    const formattedlocations = this.locations.map(location => {
      return { value: location.id, label: location.title };
    });
    return [
      {
        value: '',
        label: '----------------',
        disabled: true,
      },
      ...formattedlocations,
    ];
  }

  @computed
  get regExpQueNumber() {
    return new RegExp(this.searchValue, 'i');
  }

  @computed
  get isCurrentDay() {
    return this.activeDate.toISODate() === this.currentDate.toISODate();
  }

  @computed
  get isMobileOrTablet() {
    const devices = new RegExp(MOBILE_DEVICES, 'i');
    const msStream = 'MSStream';
    const isIOS =
      (/iPad|iPhone|iPod/.test(navigator.platform) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) &&
      (!window.hasOwnProperty(msStream) || !window[msStream]);

    return devices.test(navigator.userAgent) || isIOS;
  }

  @action.bound
  async fetchOrders(withLoading: boolean = true) {
    try {
      const requestDate = getDate(this.activeDate.toJSDate());
      const { data } = await httpFacade.onlineOrders.fetchOnlineOrders({
        from: requestDate,
        to: requestDate,
      });
      const changedOrders = data.map(items => {
        items?.orders.map(it => {
          it.deliveryTimeSlot = items.deliveryTimeSlot;
          return it;
        });
        return items;
      });
      const orders = changedOrders
        .map(group => group.orders)
        .flat()
        .slice();
      const latestOrderNumber = orders.reduce((acc, o) => {
        return Math.max(acc, o.queueNumber);
      }, 0);
      const localsLatestOrderNumber = this.ordersData.reduce((acc, o) => {
        return Math.max(acc, o.queueNumber);
      }, 0);
      if (latestOrderNumber !== localsLatestOrderNumber) {
        this.ordersData = orders.sort(
          window.location.hostname.includes('vorwerk')
            ? this.vorwerkOnlineOrdersSort
            : this.defaultOnlineOrdersSort,
        );
        this.applyFilter(this.filters);
        clearTimeout(this.markRowsTimeout);
        this.markTableRows();
      }
    } catch (error) {
      Log.warn(error);
    }
  }

  @action
  searchOrder(value) {
    this.searchValue = value;
    this.applyFilter(this.filters);
  }

  @action.bound
  changeDate(date: any) {
    this.activeDate = date;
  }

  @action.bound
  swipeDay(value: SwipeDirection) {
    this.activeDate =
      value === 'previous'
        ? this.activeDate.minus({ day: 1 })
        : this.activeDate.plus({ day: 1 });
  }

  @action
  clearInterval() {
    this.intervalFetch.stop();
  }

  @action.bound
  async orderCreated(event) {
    const eventData: NewOnlineOrdersData = JSON.parse(event.data);

    if (eventData?.type === SocketDataType.NEW_ONLINE_ORDERS) {
      const lastCreatedDate = DateTime.fromISO(
        eventData.payload.lastCreatedDate,
        { zone: 'utc' },
      );
      const newOrdersAvailable: boolean =
        typeof this.ordersData.find(
          o =>
            DateTime.fromISO(o.createdDate, { zone: 'utc' }) >= lastCreatedDate,
        ) === 'undefined';
      if (newOrdersAvailable) {
        const requestDate = getDate(this.activeDate.toJSDate());
        const { data } = await httpFacade.onlineOrders.fetchOnlineOrders({
          from: requestDate,
          to: requestDate,
          after: eventData.payload.lastCreatedDate,
        });
        const newOrders = data
          .map(items => {
            items?.orders.map(it => {
              it.deliveryTimeSlot = items.deliveryTimeSlot;
              return it;
            });
            return items;
          })
          .map(group => group.orders)
          .flat()
          .slice();
        this.ordersData = [...this.ordersData, ...newOrders];
        this.ordersData = this.ordersData
          .slice()
          .sort(
            window.location.hostname.includes('vorwerk')
              ? this.vorwerkOnlineOrdersSort
              : this.defaultOnlineOrdersSort,
          );
        this.applyFilter(this.filters);
      }
      clearTimeout(this.markRowsTimeout);
      this.markTableRows();
    } else if (
      eventData?.type === SocketDataType.DEPOSIT_ITEMS_REQUEST_CONFIRM
    ) {
      const depositItemsRequest: DepositItemsRequestConfirmData = JSON.parse(
        event.data,
      );

      this.depositItemsConfirmation.set(
        depositItemsRequest.payload.deviceName,
        depositItemsRequest.payload.pin,
      );
    } else if (
      eventData?.type === SocketDataType.DEPOSIT_ITEMS_REQUEST_COMPLETED
    ) {
      const depositItemsComplete: DepositItemsRequestCompleteData = JSON.parse(
        event.data,
      );

      this.depositItemsConfirmation.delete(
        depositItemsComplete.payload.deviceName,
      );
    }
  }

  @action.bound
  markTableRows() {
    this.markRowsTimeout = setTimeout(() => this.markTableRows(), 60000);
    this.orders = this.orders.map(o => {
      o.recentOrder =
        DateTime.fromISO(o.createdDate, { zone: 'utc' }).diffNow('seconds')
          .seconds *
          -1 <
          60 && !o.orderProcessingFinished;
      return o;
    });
  }

  @action.bound
  orderStateUpdated(id: string) {
    this.ordersData = this.ordersData
      .map(o => {
        if (o.id === id) {
          o.orderProcessingFinished = true;
        }
        return o;
      })
      .slice()
      .sort(
        window.location.hostname.includes('vorwerk')
          ? this.vorwerkOnlineOrdersSort
          : this.defaultOnlineOrdersSort,
      );
    this.applyFilter(this.filters);
    clearTimeout(this.markRowsTimeout);
    this.markTableRows();
  }

  @action.bound
  async fetchDepositItemsRequests() {
    try {
      const {
        data,
      } = await httpFacade.onlineOrders.fetchDepositItemsRequests();
      this.depositItemsConfirmation.clear();
      for (const request in data) {
        if (data.hasOwnProperty(request)) {
          this.depositItemsConfirmation.set(request, data[request]);
        }
      }
    } catch (error) {
      Log.warn(error);
    }
  }

  private defaultOnlineOrdersSort = (a: OrderModel, b: OrderModel): number => {
    if (a.orderProcessingFinished === b.orderProcessingFinished) {
      return a.queueNumber - b.queueNumber;
    } else {
      return a.orderProcessingFinished ? 1 : -1;
    }
  };

  @computed
  public get loading() {
    return this._promiseService.isPending;
  }
}

export default OnlineOrdersStore;
